(ns medplast.views.forms.login
  (:require
   [medplast.views.core :as views-core]
   [medplast.views.forms.core :refer [form labeled-input]]
   [medplast.firebase :as fb]
   [medplast.cloud-fn :as cloud-fn]
   [reagent.core :as r]
   [medplast.lang :refer-macros [go-try <?]]
   [cljs.core.async :refer [<!]]
   [reitit.frontend.easy :as rfe]))

(set! *warn-on-infer* true)

;; --- State Mutation Functions ---

(defn- set-recaptcha-verifier! [state-atom verifier]
  (swap! state-atom assoc :recaptcha-verifier verifier))

(defn- handle-mfa-verification-sent! [state-atom verification-id]
  (println "handle-mfa-verification-sent!")
  (swap! state-atom assoc
         :verification-id verification-id)
  (swap! state-atom assoc
         :step :enter-mfa-code))

(defn- set-mfa-resolver-and-hint! [state-atom resolver hint]
  (swap! state-atom assoc :resolver resolver :hint hint))

;; --- Error Handlers ---

(defn- invalid-code-error? [error]
  (= (.-code error) "auth/invalid-verification-code"))

;; --- Success Handler ---

(defn- handle-login-success! []
  (rfe/navigate :medplast.routes/frontpage))

;; --- MFA & Recaptcha Setup ---

(defn- <?start-mfa-flow!
  "Initiates the MFA flow when the login response requires it."
  [state-atom result]
  (println "start-mfa-flow!")
  (swap! state-atom assoc
         :step :wait-for-mfa)
  (println "MFA required, initiating phone verification...")
  (go-try
   (let [mfa-details (<? (fb/<?get-phone-mfa-details result))]
     (js/console.log "MFA details:" mfa-details)
     (if mfa-details
       (let [{:keys [resolver hint]} mfa-details]
         (set-mfa-resolver-and-hint! state-atom resolver hint)
         (fb/init-recaptcha-verifier! state-atom)
         (let [recaptcha-verifier (:recaptcha-verifier @state-atom)
               verification-id (<? (fb/<?verify-phone-number-for-mfa! resolver hint recaptcha-verifier))]
           (println "stat-mfa-flow! verification-id:" verification-id)
           (handle-mfa-verification-sent! state-atom verification-id)))
       (println "No phone factor hint found.")))))

(defn- mfa-required? [error]
  (and (instance? js/Error error)
       (= (aget error "code") "auth/multi-factor-auth-required")))

;; --- Validation Functions (thrown to form) ---

(defn- handle-cred-submit!
  "Attempts email/password login; throws on error for form to catch."
  [state-atom]
  (let [{:keys [email password]} @state-atom]
    (assert (and email password))
    (go-try
     (let [result (<! (fb/<?do-email-login! email password))]
       (if (instance? js/Error result)
         (if (mfa-required? result)
           (<? (<?start-mfa-flow! state-atom result))
           (throw result))
         (handle-login-success!))))))

(defn- handle-mfa-submit!
  "Attempts OTP-code MFA sign-in; throws on error for form to catch."
  [state-atom]
  (let [{:keys [resolver verification-id otp-code]} @state-atom]
    (assert (and resolver verification-id otp-code))
    (go-try
     (let [result (<! (fb/<?resolve-mfa-signin! resolver verification-id otp-code))]
       (if (instance? js/Error result)
         (if (invalid-code-error? result)
           (throw (js/Error. "Neteisingas SMS kodas."))
           (throw result))
         (handle-login-success!))))))

;; --- Login Page Component ---

(defn login-page
  []
  (r/with-let [state-atom
               (r/atom {:step :enter-credentials
                        :error nil})
               _ (add-watch state-atom :tracker
                            (fn [key ref old-state new-state]
                              #_(js/console.log (str "login-page state-atom changed from \n" old-state "\n to \n" new-state))))]
    (let [step (:step @state-atom)
          credential-phase? (= step :enter-credentials)
          credentials-hidden? (not credential-phase?)
          wait-phase? (= step :wait-for-mfa)
          wait-hidden? (not wait-phase?)
          mfa-phase? (= step :enter-mfa-code)
          mfa-hidden? (not mfa-phase?)]
      [:<>
       [form
        {:state-atom state-atom
         :<?valid-fn
         (fn [_]
           (case step
             :enter-credentials (handle-cred-submit! state-atom)
             :enter-mfa-code (handle-mfa-submit! state-atom)
             :wait-for-mfa (js/window.alert "Laukiama SMS kodo išsiuntimo...")))}
        [:div {:hidden credentials-hidden?}
         [labeled-input
          {:label "El. paštas" :state-atom state-atom :state-key :email
           :input-type :login-email :required credential-phase?}]]
        [:div {:hidden credentials-hidden?}
         [labeled-input
          {:label "Slaptažodis" :state-atom state-atom :state-key :password
           :input-type :password :required credential-phase?}]]
        [:div {:hidden wait-hidden?}
         [:p.mb-2 "Išsiunčiamas SMS kodas..."]]
        [:div {:hidden mfa-hidden?}
         [:p.mb-2 "Įveskite SMS kodą, išsiųstą į jūsų telefoną."]]
        [:div {:hidden mfa-hidden?}
         [labeled-input
          {:label "SMS Kodas" :state-atom state-atom :state-key :otp-code
           :input-type :otp :required mfa-phase?}]]]])))


(defn must-reset-password-page
  "Page when user must reset their password (has :must-reset-password claim set)."
  []
  (r/with-let [state-atom (r/atom {:new-password nil})]
    [:<>
     [form
      {:state-atom state-atom
       :<?valid-fn
       (fn [current-form-state-atom]
         (go-try
          (let [new-password (:new-password @current-form-state-atom)
                result (<! (cloud-fn/<?user-change-own-password! new-password))]
            (if (instance? js/Error result)
              (let [error-code (.-code result)
                    display-message (cond
                                      (= error-code "auth/weak-password")
                                      "Slaptažodis per silpnas. Jis turi būti bent 6 simbolių ilgio."
                                      (= error-code "auth/requires-recent-login")
                                      "Šiai operacijai reikia neseno prisijungimo. Prašome prisijungti iš naujo."
                                      :else (str "Įvyko netikėta klaida keičiant slaptažodį ("
                                                 (or error-code (.-message result)) "). Bandykite dar kartą."))]
                (throw (js/Error. display-message)))
              ;; After successful password change, log the user out
              ;; This is necessary because the server revokes refresh tokens, but doesn't immediately sign out the user
              (do
                (js/alert "Slaptažodis sėkmingai pakeistas. Jums reikės prisijungti iš naujo.")
                (fb/<?logout-user!))))))}
      [views-core/header "Pakeisti slaptažodį"]
      [:p "Jūsų slaptažodis turi būti pakeistas. Pasirinkite naują slaptažodį."]
      [labeled-input
       {:label "Naujas slaptažodis"
        :state-atom state-atom
        :state-key :new-password
        :input-type :password
        :required true}]]]))